import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function CreateQuiz({ user, existingQuiz = null, isEditMode = false }) {
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState({
    title: '',
    description: '',
    questions: [],
    time_per_question: 30,  // Tiempo por defecto 30s
    randomize_questions: false,  // Orden de preguntas
    category_id: null,  // Categoría asignada
    is_public: true,
    assigned_courses: []
  });
  const [currentQuestion, setCurrentQuestion] = useState({
    question_text: '',
    options: ['', '', '', ''],
    correct_answer: 0,
    time_limit: 30,
    points: 1000,
    image_url: null
  });
  const [uploading, setUploading] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null); // Track which question is being edited
  const [showCourseSelector, setShowCourseSelector] = useState(false);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [categories, setCategories] = useState([]);

  // Load categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/categories/${user.id}`);
        setCategories(response.data.categories || []);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    fetchCategories();
  }, [user.id]);

  // Load existing quiz data when in edit mode
  useEffect(() => {
    if (isEditMode && existingQuiz) {
      setQuiz({
        title: existingQuiz.title || '',
        description: existingQuiz.description || '',
        questions: existingQuiz.questions || [],
        time_per_question: existingQuiz.time_per_question || 30,
        randomize_questions: existingQuiz.randomize_questions || false,
        category_id: existingQuiz.category_id || null,
        is_public: existingQuiz.is_public !== undefined ? existingQuiz.is_public : true,
        assigned_courses: existingQuiz.assigned_courses || []
      });
    }
  }, [isEditMode, existingQuiz]);

  // Load teacher's courses
  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/classroom/courses/${user.id}`);
      setAvailableCourses(response.data.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      // Silently fail if not connected to Classroom
    } finally {
      setLoadingCourses(false);
    }
  };

  const handleAddQuestion = () => {
    if (!currentQuestion.question_text.trim() || currentQuestion.options.some(opt => !opt.trim())) {
      alert('Por favor completa todos los campos de la pregunta');
      return;
    }

    if (editingIndex !== null) {
      // Update existing question - keep time_limit from current question
      const updatedQuestions = [...quiz.questions];
      updatedQuestions[editingIndex] = { ...currentQuestion };
      setQuiz({
        ...quiz,
        questions: updatedQuestions
      });
      setEditingIndex(null);
    } else {
      // Add new question - use quiz global time
      setQuiz({
        ...quiz,
        questions: [...quiz.questions, { 
          ...currentQuestion,
          time_limit: quiz.time_per_question 
        }]
      });
    }

    // Reset form with current quiz time
    setCurrentQuestion({
      question_text: '',
      options: ['', '', '', ''],
      correct_answer: 0,
      time_limit: quiz.time_per_question,
      points: 1000,
      image_url: null
    });
  };

  const handleEditQuestion = (index) => {
    const questionToEdit = quiz.questions[index];
    setCurrentQuestion({ ...questionToEdit });
    setEditingIndex(index);
    // Scroll to top of form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setCurrentQuestion({
      question_text: '',
      options: ['', '', '', ''],
      correct_answer: 0,
      time_limit: 30,
      points: 1000,
      image_url: null
    });
    setEditingIndex(null);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCurrentQuestion({ ...currentQuestion, image_url: BACKEND_URL + response.data.image_url });
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error al subir la imagen');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveQuiz = async () => {
    if (!quiz.title.trim() || quiz.questions.length === 0) {
      alert('Por favor agrega un título y al menos una pregunta');
      return;
    }

    try {
      if (isEditMode && existingQuiz) {
        // Update existing quiz
        await axios.put(`${BACKEND_URL}/api/quizzes/${existingQuiz.id}`, {
          ...quiz,
          id: existingQuiz.id,
          teacher_id: existingQuiz.teacher_id,
          teacher_name: existingQuiz.teacher_name || user.name  // Preserve or add teacher name
        });
        alert('Quiz actualizado exitosamente!');
      } else {
        // Create new quiz
        await axios.post(`${BACKEND_URL}/api/quizzes`, {
          ...quiz,
          teacher_id: user.id,
          teacher_name: user.name  // Add teacher name
        });
        alert('Quiz creado exitosamente!');
      }
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving quiz:', error);
      alert(isEditMode ? 'Error al actualizar el quiz' : 'Error al crear el quiz');
    }
  };

  const handleRemoveQuestion = (index) => {
    setQuiz({
      ...quiz,
      questions: quiz.questions.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="create-quiz-page">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-primary">
              {isEditMode ? 'Editar Quiz' : 'Crear Nuevo Quiz'}
            </h1>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              data-testid="back-to-dashboard-btn"
            >
              ← Volver al Dashboard
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Quiz Info */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Información del Quiz</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Título *</label>
              <input
                type="text"
                value={quiz.title}
                onChange={(e) => setQuiz({ ...quiz, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Ej: Historia de México"
                data-testid="quiz-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
              <textarea
                value={quiz.description}
                onChange={(e) => setQuiz({ ...quiz, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Describe de qué trata este quiz..."
                rows="3"
                data-testid="quiz-description-input"
              />
            </div>

            {/* Time per Question */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tiempo por Pregunta (segundos) *
              </label>
              <div className="flex gap-2 items-center flex-wrap">
                <input
                  type="number"
                  value={quiz.time_per_question}
                  onChange={(e) => {
                    const newTime = parseInt(e.target.value) || 30;
                    setQuiz({ 
                      ...quiz, 
                      time_per_question: newTime
                    });
                  }}
                  className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  min="5"
                  max="120"
                  step="5"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setQuiz({ ...quiz, time_per_question: 10 })}
                    className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm"
                  >
                    10s
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuiz({ ...quiz, time_per_question: 15 })}
                    className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm"
                  >
                    15s
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuiz({ ...quiz, time_per_question: 20 })}
                    className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm"
                  >
                    20s
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuiz({ ...quiz, time_per_question: 30 })}
                    className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm"
                  >
                    30s
                  </button>
                </div>
                {quiz.questions.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const updatedQuestions = quiz.questions.map(q => ({
                        ...q,
                        time_limit: quiz.time_per_question
                      }));
                      setQuiz({ 
                        ...quiz, 
                        questions: updatedQuestions
                      });
                      alert(`✓ Tiempo actualizado a ${quiz.time_per_question}s en todas las ${quiz.questions.length} preguntas`);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-semibold flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Aplicar a todas ({quiz.questions.length})
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                ⚡ Recomendado: 10-15s para preguntas cortas, 20-30s para preguntas más complejas
                {quiz.questions.length > 0 && (
                  <span className="block mt-1 text-orange-600 font-medium">
                    ⚠️ Usa el botón "Aplicar a todas" para actualizar preguntas existentes
                  </span>
                )}
              </p>
            </div>

            {/* Question Order */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={quiz.randomize_questions}
                  onChange={(e) => setQuiz({ ...quiz, randomize_questions: e.target.checked })}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  🔀 Desordenar preguntas para cada sesión
                </span>
              </label>
              <p className="text-xs text-gray-500 ml-6 mt-1">
                {quiz.randomize_questions 
                  ? "✓ Activado: Las preguntas aparecerán en orden aleatorio (anti-copia)"
                  : "○ Desactivado: Las preguntas aparecerán en el orden que las creaste"
                }
              </p>
            </div>

            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🗂️ Categoría (opcional)
              </label>
              <select
                value={quiz.category_id || ''}
                onChange={(e) => setQuiz({ ...quiz, category_id: e.target.value || null })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">Sin categoría</option>
                {categories.filter(cat => !cat.parent_id).map(category => (
                  <optgroup key={category.id} label={`${category.icon} ${category.name}`}>
                    <option value={category.id} style={{ color: category.color }}>
                      {category.icon} {category.name}
                    </option>
                    {categories.filter(sub => sub.parent_id === category.id).map(subcat => (
                      <option key={subcat.id} value={subcat.id} style={{ color: subcat.color }}>
                        &nbsp;&nbsp;{subcat.icon} {subcat.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Organiza tus actividades por evaluación, tema, etc.
              </p>
            </div>

            {/* Assignment Section */}
            <div className="border-t pt-4 mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">Asignación</label>
              
              {/* Public Quiz Checkbox */}
              <div className="flex items-center mb-3">
                <input
                  type="checkbox"
                  id="is_public"
                  checked={quiz.is_public}
                  onChange={(e) => {
                    setQuiz({ 
                      ...quiz, 
                      is_public: e.target.checked,
                      assigned_courses: e.target.checked ? [] : quiz.assigned_courses
                    });
                  }}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label htmlFor="is_public" className="ml-2 text-sm text-gray-700">
                  <strong>Quiz Público</strong> - Cualquiera con el PIN puede unirse
                </label>
              </div>

              {/* Course Assignment */}
              {!quiz.is_public && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowCourseSelector(!showCourseSelector)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    {quiz.assigned_courses.length > 0 
                      ? `${quiz.assigned_courses.length} clase(s) asignada(s)` 
                      : 'Asignar a clases'}
                  </button>

                  {showCourseSelector && (
                    <div className="mt-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
                      {loadingCourses ? (
                        <p className="text-sm text-gray-600">Cargando clases...</p>
                      ) : availableCourses.length === 0 ? (
                        <p className="text-sm text-gray-600">
                          No hay clases disponibles. Conecta tu cuenta de Google Classroom primero.
                        </p>
                      ) : (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {availableCourses.map((course) => (
                            <div key={course.id} className="flex items-center">
                              <input
                                type="checkbox"
                                id={`course_${course.id}`}
                                checked={quiz.assigned_courses.includes(course.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setQuiz({
                                      ...quiz,
                                      assigned_courses: [...quiz.assigned_courses, course.id]
                                    });
                                  } else {
                                    setQuiz({
                                      ...quiz,
                                      assigned_courses: quiz.assigned_courses.filter(id => id !== course.id)
                                    });
                                  }
                                }}
                                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                              />
                              <label htmlFor={`course_${course.id}`} className="ml-2 text-sm text-gray-700">
                                {course.name} {course.section && `(${course.section})`}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Selected Courses Display */}
                  {quiz.assigned_courses.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {quiz.assigned_courses.map((courseId) => {
                        const course = availableCourses.find(c => c.id === courseId);
                        return course ? (
                          <span key={courseId} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                            {course.name}
                            <button
                              onClick={() => {
                                setQuiz({
                                  ...quiz,
                                  assigned_courses: quiz.assigned_courses.filter(id => id !== courseId)
                                });
                              }}
                              className="ml-2 hover:text-blue-600"
                            >
                              ×
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Add Question Form */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">
              {editingIndex !== null ? 'Editar Pregunta' : 'Agregar Pregunta'}
            </h2>
            {editingIndex !== null && (
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                data-testid="cancel-edit-btn"
              >
                Cancelar Edición
              </button>
            )}
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Pregunta *</label>
              <input
                type="text"
                value={currentQuestion.question_text}
                onChange={(e) => setCurrentQuestion({ ...currentQuestion, question_text: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Escribe tu pregunta aquí..."
                data-testid="question-text-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Opciones *</label>
              <div className="space-y-2">
                {currentQuestion.options.map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="correct"
                      checked={currentQuestion.correct_answer === index}
                      onChange={() => setCurrentQuestion({ ...currentQuestion, correct_answer: index })}
                      className="w-4 h-4 text-primary"
                      data-testid={`correct-answer-radio-${index}`}
                    />
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...currentQuestion.options];
                        newOptions[index] = e.target.value;
                        setCurrentQuestion({ ...currentQuestion, options: newOptions });
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder={`Opción ${index + 1}`}
                      data-testid={`option-input-${index}`}
                    />
                  </div>
                ))}
                <p className="text-xs text-gray-500">Selecciona el radio button de la respuesta correcta</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tiempo límite (segundos)</label>
                <input
                  type="number"
                  value={currentQuestion.time_limit}
                  onChange={(e) => setCurrentQuestion({ ...currentQuestion, time_limit: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  min="10"
                  max="120"
                  data-testid="time-limit-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Puntos</label>
                <input
                  type="number"
                  value={currentQuestion.points}
                  onChange={(e) => setCurrentQuestion({ ...currentQuestion, points: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  min="100"
                  max="2000"
                  step="100"
                  data-testid="points-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Imagen (opcional)</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploading}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                data-testid="image-upload-input"
              />
              {uploading && <p className="text-sm text-gray-500 mt-1">Subiendo imagen...</p>}
              {currentQuestion.image_url && (
                <img src={currentQuestion.image_url} alt="Preview" className="mt-2 h-32 object-cover rounded-lg" />
              )}
            </div>

            <button
              onClick={handleAddQuestion}
              className={`w-full px-6 py-3 text-white rounded-lg font-semibold transition ${
                editingIndex !== null 
                  ? 'bg-blue-600 hover:bg-blue-700' 
                  : 'bg-accent hover:bg-teal-500'
              }`}
              data-testid="add-question-btn"
            >
              {editingIndex !== null ? '✓ Actualizar Pregunta' : '+ Agregar Pregunta'}
            </button>
          </div>
        </div>

        {/* Questions List */}
        {quiz.questions.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Preguntas Agregadas ({quiz.questions.length})</h2>
            <div className="space-y-4">
              {quiz.questions.map((q, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4" data-testid={`question-item-${index}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-800 mb-2">{index + 1}. {q.question_text}</h4>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        {q.options.map((opt, i) => (
                          <div key={i} className={`text-sm p-2 rounded ${i === q.correct_answer ? 'bg-green-100 text-green-800 font-semibold' : 'bg-gray-100 text-gray-700'}`}>
                            {opt}
                          </div>
                        ))}
                      </div>
                      <div className="text-xs text-gray-500">
                        {q.time_limit}s • {q.points} puntos
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleEditQuestion(index)}
                        className="text-blue-600 hover:text-blue-800 p-2"
                        data-testid={`edit-question-btn-${index}`}
                        title="Editar pregunta"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleRemoveQuestion(index)}
                        className="text-danger hover:text-red-600 p-2"
                        data-testid={`remove-question-btn-${index}`}
                        title="Eliminar pregunta"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSaveQuiz}
            disabled={quiz.questions.length === 0}
            className="px-8 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="save-quiz-btn"
          >
            {isEditMode ? 'Actualizar Quiz' : 'Guardar Quiz'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateQuiz;
